import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { mapOpeningSoonIpos } from '../../utils/openingSoonIpos';

const initialState = {
  // Raw items from the API; views map them through the feed's selector
  items: [],
  refreshedAt: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

/**
 * One "confirmed bidding dates" feed: a thunk, its reducer and a selector of
 * soonest-first rows. Both feeds share the NSE row shape, so everything but the
 * endpoint and state key is the same.
 *
 * The thunk takes options.maxAgeMs: skip the request when data this recent is already loaded.
 */
const createUpcomingFeed = ({ stateKey, typePrefix, request, label }) => {
  const fetchFeed = createAsyncThunk(
    typePrefix,
    async (_options, { rejectWithValue }) => {
      try {
        const data = await request();
        if (data?.success === false) {
          return rejectWithValue(data.error || `Failed to fetch ${label}`);
        }
        return data;
      } catch (error) {
        return rejectWithValue(error.message || `Failed to fetch ${label}`);
      }
    },
    {
      condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
        const { status, fetchedAt } = getState()[stateKey];
        // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
        if (status === 'loading') return false;
        if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
          return false;
        }
        return true;
      },
    }
  );

  const slice = createSlice({
    name: stateKey,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchFeed.pending, (state) => {
          state.status = 'loading';
          state.error = null;
        })
        .addCase(fetchFeed.fulfilled, (state, action) => {
          const data = action.payload?.data || {};
          state.status = 'succeeded';
          state.items = Array.isArray(data.items) ? data.items : [];
          state.refreshedAt = data.refreshed_at || null;
          state.fetchedAt = Date.now();
        })
        .addCase(fetchFeed.rejected, (state, action) => {
          // Keep previously loaded items so a failed refresh doesn't blank the section
          state.status = 'failed';
          state.error = action.payload || `Failed to load ${label}`;
        });
    },
  });

  const selectRows = createSelector([(state) => state[stateKey].items], (items) => mapOpeningSoonIpos(items));

  return { fetchFeed, reducer: slice.reducer, selectRows };
};

// GET /api/market/ipos/upcoming -- mainboard and SME issues (state.upcomingIpos)
const upcomingFeed = createUpcomingFeed({
  stateKey: 'upcomingIpos',
  typePrefix: 'upcomingIpos/fetchUpcomingIpos',
  request: () => ipoService.getUpcomingIpos(),
  label: 'upcoming IPOs',
});

// GET /api/market/ipos/sme/upcoming -- SME issues only (state.smeUpcomingIpos)
const smeUpcomingFeed = createUpcomingFeed({
  stateKey: 'smeUpcomingIpos',
  typePrefix: 'smeUpcomingIpos/fetchSmeUpcomingIpos',
  request: () => ipoService.getSmeUpcomingIpos(),
  label: 'upcoming SME IPOs',
});

export const fetchUpcomingIpos = upcomingFeed.fetchFeed;
// Soonest-first rows for the Upcoming IPOs page's "Opening Soon" section
export const selectOpeningSoonIpos = upcomingFeed.selectRows;

export const fetchSmeUpcomingIpos = smeUpcomingFeed.fetchFeed;
// Soonest-first rows for the Upcoming SME IPOs page
export const selectSmeOpeningSoonIpos = smeUpcomingFeed.selectRows;
export const smeUpcomingIposReducer = smeUpcomingFeed.reducer;

export default upcomingFeed.reducer;
